/**
 * Boîte noire d'une session écran (spec wiki `specs/boite-noire-session.md`).
 *
 * Accumule en mémoire les signaux de santé d'une `screenSession` — décrochages de
 * manettes, coupures de l'écran, latence écran ↔ serveur, gels de la boucle JS,
 * rafales de connexions, cause de fin — et les rend sous une forme prête à être
 * écrite dans le champ `health` du doc Firestore à chaque heartbeat.
 *
 * Classe PURE : aucune dépendance Firebase / lib / DOM, l'horloge est injectée
 * (`now`) pour être testable. C'est `FirestoreScreenDataCollector` qui la nourrit.
 *
 * Décision produit (2026-09-08) : rien de tout ça n'est montré au client. Ces
 * données servent l'alerte interne et l'analyse après coup (cf soirée Top Music du
 * 29/08/2026 : 238 joueurs, fin 13 min après une rafale de 177 connexions, et
 * aucune trace exploitable).
 */

/** Pourquoi `stop()` a été appelé. `abrupt` n'est JAMAIS écrit : c'est l'absence
 * d'`endReason` sur une session dont `lastHeartbeat` a plus de 2 heartbeats qui le
 * signifie — une règle de lecture, robuste au crash / coupure de courant, là où un
 * événement de fermeture navigateur (beforeunload) peut ne pas être délivré. */
/** `other_tab` : onglet mis en veille, le même bar est ouvert dans un autre onglet (screenTabLock). */
export type EndReason = 'unmount' | 'page_unload' | 'navigation' | 'session_closed' | 'other_tab';

export type HealthTimelinePoint = { t: number; players: number };

export type SessionHealth = {
    /** = maxPlayers du doc, regroupé ici pour lire la santé d'un seul bloc. */
    peakPlayers: number;
    /** Max de premières connexions (onConnect) sur une fenêtre glissante de 5 min. */
    joinBurstMax5m: number;
    /** onDeviceAway : manettes parties en grace period (décrochages). */
    gamepadAway: number;
    /** onDeviceBack : manettes revenues. away − back ≈ pertes sèches. */
    gamepadBack: number;
    /** onTemporaryDisconnect côté écran : coupures du socket de l'écran. */
    hostBlips: number;
    /** Temps cumulé (ms) écran hors ligne, blip en cours inclus au snapshot. */
    hostAwayMs: number;
    /** Connexion de l'écran morte sans bruit, reconnexion forcée par le veilleur
     * (ScreenStaleLinkReconnect) au lieu d'attendre le ping timeout (~40 s). */
    staleReconnects: number;
    /** Même détection, budget de reconnexions épuisé : observée sans agir. */
    staleObserved: number;
    /** Sonde écho écran → serveur → écran. null tant qu'aucun échantillon. */
    rtt: { p50: number; max: number; samples: number } | null;
    /** Trous > FREEZE_MS dans la boucle JS, onglet visible (gel réel de l'écran). */
    screenFreezes: number;
    screenFreezeMaxMs: number;
    /** Même chose onglet caché (TV sur une autre source, throttling navigateur). */
    screenHiddenFreezes: number;
    /** Un point par heartbeat, `t` en secondes epoch, plafonné aux TIMELINE_MAX derniers. */
    timeline: HealthTimelinePoint[];
    endReason?: EndReason;
};

function percentile(values: number[], p: number): number {
    if (!values.length) {
        return 0;
    }
    const sorted = [...values].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
    return Math.round(sorted[idx]);
}

export class SessionHealthTracker {
    /** Fenêtre glissante de la rafale de connexions. */
    static readonly BURST_WINDOW_MS = 5 * 60 * 1000;

    /** Écart entre deux ticks du watchdog au-delà duquel on compte un gel. */
    static readonly FREEZE_MS = 5000;

    /** 120 points × 3 min = 6 h de soirée. Au-delà on garde les plus récents. */
    static readonly TIMELINE_MAX = 120;

    /** Échantillons RTT gardés pour le p50 (les plus récents) ; le compteur, lui, est total. */
    static readonly RTT_KEEP = 300;

    private readonly _now: () => number;

    private _joins: number[] = [];

    private _joinBurstMax = 0;

    private _gamepadAway = 0;

    private _gamepadBack = 0;

    private _hostBlips = 0;

    private _hostAwayMs = 0;

    private _hostAwaySince?: number;

    private _staleReconnects = 0;

    private _staleObserved = 0;

    private _rtt: number[] = [];

    private _rttSamples = 0;

    private _rttMax = 0;

    private _freezes = 0;

    private _freezeMaxMs = 0;

    private _hiddenFreezes = 0;

    private _lastTick?: number;

    private _timeline: HealthTimelinePoint[] = [];

    private _endReason?: EndReason;

    constructor(now: () => number = () => Date.now()) {
        this._now = now;
    }

    /** Première connexion d'une manette (pas un retour de grace period). */
    noteConnect() {
        const now = this._now();
        this._joins.push(now);
        const floor = now - SessionHealthTracker.BURST_WINDOW_MS;
        while (this._joins.length && this._joins[0] < floor) {
            this._joins.shift();
        }
        if (this._joins.length > this._joinBurstMax) {
            this._joinBurstMax = this._joins.length;
        }
    }

    noteGamepadAway() {
        this._gamepadAway += 1;
    }

    noteGamepadBack() {
        this._gamepadBack += 1;
    }

    /** Le socket de l'écran est tombé (onTemporaryDisconnect). Idempotent tant que
     * l'écran n'est pas revenu : un 2e signal pendant le même blip ne recompte pas. */
    noteHostAway() {
        if (this._hostAwaySince !== undefined) {
            return;
        }
        this._hostBlips += 1;
        this._hostAwaySince = this._now();
    }

    /** L'écran a repris (onReady). No-op s'il n'était pas parti — onReady est aussi
     * dispatché à la toute première connexion. */
    noteHostBack() {
        if (this._hostAwaySince === undefined) {
            return;
        }
        this._hostAwayMs += this._now() - this._hostAwaySince;
        this._hostAwaySince = undefined;
    }

    /** Le veilleur de lien a constaté un silence de l'écran (`acted` : reconnexion forcée). */
    noteStaleLink(acted: boolean) {
        if (acted) {
            this._staleReconnects += 1;
        } else {
            this._staleObserved += 1;
        }
    }

    noteRtt(ms: number) {
        if (!Number.isFinite(ms) || ms < 0) {
            return;
        }
        this._rttSamples += 1;
        if (ms > this._rttMax) {
            this._rttMax = ms;
        }
        this._rtt.push(ms);
        if (this._rtt.length > SessionHealthTracker.RTT_KEEP) {
            this._rtt.shift();
        }
    }

    /** Tick du watchdog (censé arriver toutes les secondes). Un écart > FREEZE_MS
     * depuis le tick précédent = la boucle JS n'a pas tourné : gel de l'écran, ou
     * onglet caché/throttlé si `hidden`. Le premier tick ne fait qu'armer. */
    tick(hidden: boolean) {
        const now = this._now();
        if (this._lastTick !== undefined) {
            const gap = now - this._lastTick;
            if (gap > SessionHealthTracker.FREEZE_MS) {
                if (hidden) {
                    this._hiddenFreezes += 1;
                } else {
                    this._freezes += 1;
                    if (gap > this._freezeMaxMs) {
                        this._freezeMaxMs = gap;
                    }
                }
            }
        }
        this._lastTick = now;
    }

    /** Un point de timeline (à appeler au heartbeat). */
    notePlayers(players: number) {
        this._timeline.push({ t: Math.floor(this._now() / 1000), players });
        if (this._timeline.length > SessionHealthTracker.TIMELINE_MAX) {
            this._timeline.shift();
        }
    }

    setEndReason(reason: EndReason) {
        this._endReason = reason;
    }

    get endReason(): EndReason | undefined {
        return this._endReason;
    }

    /** Vue prête à écrire. `peakPlayers` vient du collecteur (source de vérité maxPlayers). */
    snapshot(peakPlayers: number): SessionHealth {
        const health: SessionHealth = {
            peakPlayers,
            joinBurstMax5m: this._joinBurstMax,
            gamepadAway: this._gamepadAway,
            gamepadBack: this._gamepadBack,
            hostBlips: this._hostBlips,
            hostAwayMs: this._hostAwayMs + (this._hostAwaySince !== undefined ? this._now() - this._hostAwaySince : 0),
            staleReconnects: this._staleReconnects,
            staleObserved: this._staleObserved,
            rtt: this._rttSamples
                ? { p50: percentile(this._rtt, 50), max: this._rttMax, samples: this._rttSamples }
                : null,
            screenFreezes: this._freezes,
            screenFreezeMaxMs: this._freezeMaxMs,
            screenHiddenFreezes: this._hiddenFreezes,
            timeline: [...this._timeline],
        };
        // Firestore refuse `undefined` : la clé n'existe que si une raison a été posée.
        if (this._endReason) {
            health.endReason = this._endReason;
        }
        return health;
    }
}
